import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  writeBatch
} from 'firebase/firestore'
import { localizedValue, stripHtml, summarizeText } from '../../helpers/seo'
import { FIRESTORE_COLLECTIONS } from '../data/firebaseModel'
import { firestore } from './firebaseClient'

const emptySeo = () => ({
  title: { ua: '', ru: '', en: '' },
  description: { ua: '', ru: '', en: '' },
  keywords: { ua: '', ru: '', en: '' }
})

function page(label, title, description, keywords) {
  return {
    label,
    seo: { title, description, keywords },
    customTags: []
  }
}

export const BASE_SITE_SEO_DOCUMENTS = Object.freeze({
  home: page(
    'Головна',
    {
      ua: 'Optometry Academy — освіта для оптометристів',
      ru: 'Optometry Academy — образование для оптометристов',
      en: 'Optometry Academy — education for optometrists'
    },
    {
      ua: 'Optometry Academy — освітня платформа для фахівців з оптометрії: актуальні курси, професійні події, новини галузі та експертна спільнота.',
      ru: 'Optometry Academy — образовательная платформа для специалистов по оптометрии: актуальные курсы, профессиональные события, новости и эксперты.',
      en: 'Optometry Academy is an education platform for optometry professionals featuring current courses, industry events, expert insights and news.'
    },
    {
      ua: 'Optometry Academy, оптометрія, навчання, курси для оптометристів',
      ru: 'Optometry Academy, оптометрия, обучение, курсы для оптометристов',
      en: 'Optometry Academy, optometry, education, optometry courses'
    }
  ),
  about: page(
    'Про академію',
    {
      ua: 'Про Optometry Academy',
      ru: 'Об Optometry Academy',
      en: 'About Optometry Academy'
    },
    {
      ua: 'Дізнайтеся про місію, історію та цінності Optometry Academy — професійної освітньої платформи, що розвиває спільноту фахівців з оптометрії.',
      ru: 'Узнайте о миссии, истории и ценностях Optometry Academy — профессиональной образовательной платформы, развивающей сообщество оптометристов.',
      en: 'Discover the mission, history and values of Optometry Academy, a professional education platform advancing the optometry community.'
    },
    {
      ua: 'про академію, Optometry Academy, місія, оптометрія',
      ru: 'об академии, Optometry Academy, миссия, оптометрия',
      en: 'about academy, Optometry Academy, mission, optometry'
    }
  ),
  courses: page(
    'Курси',
    {
      ua: 'Курси з оптометрії | Optometry Academy',
      ru: 'Курсы по оптометрии | Optometry Academy',
      en: 'Optometry courses | Optometry Academy'
    },
    {
      ua: 'Оберіть професійний курс з оптометрії, поглиблюйте практичні знання та навчайтеся у досвідчених викладачів Optometry Academy у зручному форматі.',
      ru: 'Выберите профессиональный курс по оптометрии, углубляйте практические знания и учитесь у опытных преподавателей Optometry Academy.',
      en: 'Choose a professional optometry course, strengthen practical skills and learn from experienced Optometry Academy educators in a convenient format.'
    },
    {
      ua: 'курси з оптометрії, навчання оптометристів, професійна освіта',
      ru: 'курсы по оптометрии, обучение оптометристов, профессиональное образование',
      en: 'optometry courses, optometrist training, professional education'
    }
  ),
  events: page(
    'Події',
    {
      ua: 'Події для оптометристів | Optometry Academy',
      ru: 'События для оптометристов | Optometry Academy',
      en: 'Optometry events | Optometry Academy'
    },
    {
      ua: 'Актуальні вебінари, лекції та професійні зустрічі для оптометристів. Переглядайте програму подій Optometry Academy та реєструйтеся онлайн.',
      ru: 'Актуальные вебинары, лекции и профессиональные встречи для оптометристов. Смотрите программу Optometry Academy и регистрируйтесь онлайн.',
      en: 'Explore current webinars, lectures and professional meetings for optometrists. View the Optometry Academy event schedule and register online.'
    },
    {
      ua: 'події для оптометристів, вебінари, лекції, оптометрія',
      ru: 'события для оптометристов, вебинары, лекции, оптометрия',
      en: 'optometry events, webinars, lectures, optometry'
    }
  ),
  news: page(
    'Новини',
    {
      ua: 'Новини оптометрії | Optometry Academy',
      ru: 'Новости оптометрии | Optometry Academy',
      en: 'Optometry news | Optometry Academy'
    },
    {
      ua: 'Читайте новини, експертні статті та практичні матеріали про оптометрію, професійне навчання, сучасні методики й розвиток галузі.',
      ru: 'Читайте новости, экспертные статьи и практические материалы об оптометрии, профессиональном обучении, современных методиках и развитии отрасли.',
      en: 'Read optometry news, expert articles and practical resources covering professional education, modern methods and developments across the field.'
    },
    {
      ua: 'новини оптометрії, статті, експертні матеріали, Optometry Academy',
      ru: 'новости оптометрии, статьи, экспертные материалы, Optometry Academy',
      en: 'optometry news, articles, expert insights, Optometry Academy'
    }
  ),
  team: page(
    'Команда',
    {
      ua: 'Команда експертів | Optometry Academy',
      ru: 'Команда экспертов | Optometry Academy',
      en: 'Expert team | Optometry Academy'
    },
    {
      ua: 'Познайомтеся з викладачами та експертами Optometry Academy: досвідченими фахівцями, які діляться практичними знаннями у сфері оптометрії.',
      ru: 'Познакомьтесь с преподавателями и экспертами Optometry Academy — опытными специалистами, которые делятся практическими знаниями по оптометрии.',
      en: 'Meet the Optometry Academy educators and experts: experienced professionals who share practical knowledge and advance the field of optometry.'
    },
    {
      ua: 'команда Optometry Academy, викладачі, експерти, оптометристи',
      ru: 'команда Optometry Academy, преподаватели, эксперты, оптометристы',
      en: 'Optometry Academy team, educators, experts, optometrists'
    }
  ),
  contacts: page(
    'Контакти',
    {
      ua: 'Контакти | Optometry Academy',
      ru: 'Контакты | Optometry Academy',
      en: 'Contacts | Optometry Academy'
    },
    {
      ua: 'Зв’яжіться з командою Optometry Academy щодо навчання, курсів, подій і партнерства. Знайдіть актуальні контакти та зручний спосіб для звернення.',
      ru: 'Свяжитесь с командой Optometry Academy по вопросам обучения, курсов, событий и партнерства. Найдите актуальные контакты и удобный способ связи.',
      en: 'Contact the Optometry Academy team about education, courses, events or partnerships. Find current details and choose a convenient way to reach us.'
    },
    {
      ua: 'контакти Optometry Academy, зв’язок, навчання, оптометрія',
      ru: 'контакты Optometry Academy, связь, обучение, оптометрия',
      en: 'Optometry Academy contacts, support, education, optometry'
    }
  ),
  login: page(
    'Вхід',
    {
      ua: 'Вхід до акаунта | Optometry Academy',
      ru: 'Вход в аккаунт | Optometry Academy',
      en: 'Sign in | Optometry Academy'
    },
    {
      ua: 'Увійдіть до особистого кабінету Optometry Academy, щоб продовжити навчання, переглядати доступні курси, матеріали та результати тестів.',
      ru: 'Войдите в личный кабинет Optometry Academy, чтобы продолжить обучение, просматривать доступные курсы, материалы и результаты тестов.',
      en: 'Sign in to your Optometry Academy account to continue learning and access your available courses, materials, progress and test results.'
    },
    {
      ua: 'вхід Optometry Academy, особистий кабінет, акаунт студента',
      ru: 'вход Optometry Academy, личный кабинет, аккаунт студента',
      en: 'Optometry Academy sign in, student account, learning profile'
    }
  ),
  registration: page(
    'Реєстрація',
    {
      ua: 'Реєстрація | Optometry Academy',
      ru: 'Регистрация | Optometry Academy',
      en: 'Registration | Optometry Academy'
    },
    {
      ua: 'Створіть акаунт в Optometry Academy, щоб записуватися на професійні курси й події, отримувати навчальні матеріали та відстежувати прогрес.',
      ru: 'Создайте аккаунт в Optometry Academy, чтобы записываться на профессиональные курсы и события, получать материалы и отслеживать прогресс.',
      en: 'Create an Optometry Academy account to enroll in professional courses and events, access learning materials and track your educational progress.'
    },
    {
      ua: 'реєстрація Optometry Academy, створити акаунт, навчання',
      ru: 'регистрация Optometry Academy, создать аккаунт, обучение',
      en: 'Optometry Academy registration, create account, education'
    }
  ),
  'password-recovery': page(
    'Відновлення пароля',
    {
      ua: 'Відновлення пароля | Optometry Academy',
      ru: 'Восстановление пароля | Optometry Academy',
      en: 'Password recovery | Optometry Academy'
    },
    {
      ua: 'Відновіть доступ до особистого кабінету Optometry Academy. Вкажіть електронну адресу та отримайте інструкції для створення нового пароля.',
      ru: 'Восстановите доступ к личному кабинету Optometry Academy. Укажите электронную почту и получите инструкции для создания нового пароля.',
      en: 'Restore access to your Optometry Academy account. Enter your email address and receive instructions for creating a new secure password.'
    },
    {
      ua: 'відновлення пароля, доступ до акаунта, Optometry Academy',
      ru: 'восстановление пароля, доступ к аккаунту, Optometry Academy',
      en: 'password recovery, account access, Optometry Academy'
    }
  ),
  'not-found': page(
    'Сторінку не знайдено',
    {
      ua: 'Сторінку не знайдено | Optometry Academy',
      ru: 'Страница не найдена | Optometry Academy',
      en: 'Page not found | Optometry Academy'
    },
    {
      ua: 'Запитану сторінку Optometry Academy не знайдено. Перевірте адресу або поверніться на головну, щоб перейти до курсів, подій та матеріалів.',
      ru: 'Запрошенная страница Optometry Academy не найдена. Проверьте адрес или вернитесь на главную, чтобы перейти к курсам, событиям и материалам.',
      en: 'The requested Optometry Academy page could not be found. Check the address or return home to browse courses, events and educational resources.'
    },
    {
      ua: 'сторінку не знайдено, помилка 404, Optometry Academy',
      ru: 'страница не найдена, ошибка 404, Optometry Academy',
      en: 'page not found, 404 error, Optometry Academy'
    }
  ),
  profile: page(
    'Профіль користувача',
    {
      ua: '{userName} — профіль | {siteName}',
      ru: '{userName} — профиль | {siteName}',
      en: '{userName} — profile | {siteName}'
    },
    {
      ua: 'Профіль користувача {userName} на платформі {siteName}: доступні курси, навчальні матеріали, прогрес і результати проходження тестів.',
      ru: 'Профиль пользователя {userName} на платформе {siteName}: доступные курсы, учебные материалы, прогресс и результаты прохождения тестов.',
      en: 'The {userName} profile on {siteName}: available courses, learning materials, educational progress and completed test results in one place.'
    },
    {
      ua: '{userName}, профіль, навчання, {siteName}',
      ru: '{userName}, профиль, обучение, {siteName}',
      en: '{userName}, profile, education, {siteName}'
    }
  ),
  'course-test-attempt': page(
    'Проходження тесту',
    {
      ua: '{testTitle} — {courseTitle} | {siteName}',
      ru: '{testTitle} — {courseTitle} | {siteName}',
      en: '{testTitle} — {courseTitle} | {siteName}'
    },
    {
      ua: 'Пройдіть тест для перевірки знань за курсом «{courseTitle}».',
      ru: 'Пройдите тест для проверки знаний по курсу «{courseTitle}».',
      en: 'Take the test to assess your knowledge of the “{courseTitle}” course.'
    },
    {
      ua: '{courseTitle}, {testTitle}, тест, навчання, оптометрія',
      ru: '{courseTitle}, {testTitle}, тест, обучение, оптометрия',
      en: '{courseTitle}, {testTitle}, test, education, optometry'
    }
  ),
  global: {
    customTags: []
  }
})

export const SITE_SEO_ROUTES = Object.freeze({
  home: '/',
  about: '/about',
  courses: '/courses',
  events: '/events',
  news: '/news',
  team: '/team',
  contacts: '/contacts',
  login: '/login',
  registration: '/registration',
  'password-recovery': '/password-recovery',
  'not-found': '404',
  profile: '/profile/:id',
  'course-test-attempt': '/courses/:id/test/:startedCourseId/:attemptId'
})

function additionalPageSeo(pageItem) {
  const seo = emptySeo()
  ;['ua', 'ru', 'en'].forEach(lang => {
    const title = localizedValue(pageItem.title, lang)
    const text = stripHtml(localizedValue(pageItem.text, lang))
    seo.title[lang] = summarizeText(title, 60)
    seo.description[lang] = summarizeText(text, 170)
    seo.keywords[lang] = [title, 'Optometry Academy', lang === 'en' ? 'optometry' : (lang === 'ru' ? 'оптометрия' : 'оптометрія')]
      .filter(Boolean)
      .join(', ')
  })
  return {
    label: localizedValue(pageItem.title, 'ua') || `Додаткова сторінка ${pageItem.id}`,
    seo,
    customTags: []
  }
}

export function additionalSeoDocumentId(additionalPageId) {
  return `additional__${additionalPageId}`
}

function expectedDocumentIds(additionalPages = []) {
  return [
    ...Object.keys(BASE_SITE_SEO_DOCUMENTS),
    ...additionalPages.map(pageItem => additionalSeoDocumentId(pageItem.id))
  ]
}

export async function fetchSiteSeoDocuments(additionalPages = []) {
  const snapshots = await Promise.all(
    expectedDocumentIds(additionalPages)
      .map(documentId => getDoc(doc(firestore, FIRESTORE_COLLECTIONS.siteSeo, documentId)))
  )
  return snapshots.reduce((result, documentSnapshot) => {
    if (documentSnapshot.exists()) {
      result[documentSnapshot.id] = {
        id: documentSnapshot.id,
        ...documentSnapshot.data()
      }
    }
    return result
  }, {})
}

export async function seedMissingSiteSeoDocuments(additionalPages = []) {
  const existing = await fetchSiteSeoDocuments(additionalPages)
  const desired = {
    ...BASE_SITE_SEO_DOCUMENTS,
    ...additionalPages.reduce((result, pageItem) => {
      result[additionalSeoDocumentId(pageItem.id)] = additionalPageSeo(pageItem)
      return result
    }, {})
  }
  const missingEntries = Object.entries(desired).filter(([id]) => !existing[id])

  for (let offset = 0; offset < missingEntries.length; offset += 450) {
    const batch = writeBatch(firestore)
    missingEntries.slice(offset, offset + 450).forEach(([id, value]) => {
      batch.set(doc(firestore, FIRESTORE_COLLECTIONS.siteSeo, id), {
        ...value,
        updatedAt: serverTimestamp()
      })
    })
    await batch.commit()
  }

  return fetchSiteSeoDocuments(additionalPages)
}

function normalizedLocalized(value) {
  return {
    ua: localizedValue(value && value.ua),
    ru: localizedValue(value && value.ru),
    en: localizedValue(value && value.en)
  }
}

function normalizedTags(tags) {
  return (Array.isArray(tags) ? tags : []).map(tag => ({
    id: String(tag.id || ''),
    name: String(tag.name || ''),
    enabled: Boolean(tag.enabled),
    placement: ['head', 'bodyStart', 'bodyEnd'].includes(tag.placement) ? tag.placement : 'head',
    html: String(tag.html || '')
  }))
}

export async function saveSiteSeoDocument(documentId, value) {
  const payload = documentId === 'global'
    ? { customTags: normalizedTags(value.customTags) }
    : {
        label: String(value.label || ''),
        seo: {
          title: normalizedLocalized(value.seo && value.seo.title),
          description: normalizedLocalized(value.seo && value.seo.description),
          keywords: normalizedLocalized(value.seo && value.seo.keywords)
        },
        customTags: normalizedTags(value.customTags)
      }

  await setDoc(doc(firestore, FIRESTORE_COLLECTIONS.siteSeo, documentId), {
    ...payload,
    updatedAt: serverTimestamp()
  }, { merge: false })
}
